import { router } from '@inertiajs/react';
import AppLayout from '@/layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import {
  ArrowLeft,
  Award,
  Calendar,
  User,
  Hash,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";

function CelebrationAwardShow({ celebration_award }) {
  const goBack = () => {
    router.visit('/admin/celebration_awards');
  };

  const getStatusInfo = (status) => {
    const configs = {
      completed: {
        icon: CheckCircle,
        text: 'Completado',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200',
        iconColor: 'text-green-500'
      },
      failed: {
        icon: AlertCircle,
        text: 'Fallido',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200',
        iconColor: 'text-red-500'
      },
      skipped: {
        icon: AlertCircle,
        text: 'Omitido',
        bgColor: 'bg-gray-50',
        textColor: 'text-gray-700',
        borderColor: 'border-gray-200',
        iconColor: 'text-gray-500'
      }
    };

    return configs[status] || configs.skipped;
  };

  const statusInfo = getStatusInfo(celebration_award.status);
  const StatusIcon = statusInfo.icon;

  const getEventTypeEmoji = (triggerType) => {
    const emojis = {
      birthday: '🎂',
      work_anniversary: '🎉',
      new_user: '👋',
      custom_date: '📅'
    };
    return emojis[triggerType] || '🎁';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString) => {
    return new Date(dateTimeString).toLocaleString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button
          onClick={goBack}
          variant="ghost"
          size="sm"
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Historial
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Detalle de Celebración</h1>
          <p className="text-sm text-gray-500">ID: #{celebration_award.id}</p>
        </div>
      </div>

      <Card className={cn('border-t-4', statusInfo.borderColor)}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={cn('w-16 h-16 rounded-full flex items-center justify-center', statusInfo.bgColor)}>
                <StatusIcon className={cn('w-8 h-8', statusInfo.iconColor)} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Estado: {statusInfo.text}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Creado el {formatDateTime(celebration_award.created_at)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-orange-600">
                {celebration_award.points_awarded} pts
              </div>
              <p className="text-xs text-gray-500 mt-1">Puntos otorgados</p>
            </div>
          </div>

          {celebration_award.notes && (
            <div className={cn('mt-4 p-4 rounded-lg', statusInfo.bgColor, statusInfo.borderColor, 'border')}>
              <p className={cn('text-sm font-medium mb-1', statusInfo.textColor)}>Notas:</p>
              <p className="text-sm text-gray-700">{celebration_award.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <User className="w-5 h-5 mr-2 text-blue-500" />
              Usuario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              {celebration_award.user.avatar_url ? (
                <img
                  src={celebration_award.user.avatar_url}
                  alt={celebration_award.user.full_name}
                  className="w-16 h-16 rounded-full border-2 border-gray-200"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-2xl border-2 border-gray-200">
                  {celebration_award.user.full_name.charAt(0)}
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-900 text-lg">
                  {celebration_award.user.full_name}
                </h3>
                <p className="text-sm text-gray-600">{celebration_award.user.email}</p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">ID de Usuario:</span>
                <span className="font-medium text-gray-900">#{celebration_award.user.id}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <Award className="w-5 h-5 mr-2 text-purple-500" />
              Evento de Celebración
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-4">
              <div className="text-4xl">
                {getEventTypeEmoji(celebration_award.celebration_event.event_type)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {celebration_award.celebration_event.event_type}
                </h3>
                {celebration_award.celebration_event.hashtag && (
                  <p className="text-sm text-purple-600 flex items-center mt-1">
                    <Hash className="w-3 h-3 mr-1" />
                    {celebration_award.celebration_event.hashtag}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">ID de Evento:</span>
                <span className="font-medium text-gray-900">#{celebration_award.celebration_event.id}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Puntos Base:</span>
                <span className="font-medium text-orange-600">
                  {celebration_award.celebration_event.points_amount} pts
                </span>
              </div>
            </div>

            {celebration_award.celebration_event.message_template && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600 font-medium mb-1">Plantilla de Mensaje:</p>
                <p className="text-sm text-gray-700 italic">
                  "{celebration_award.celebration_event.message_template}"
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {celebration_award.recognition && (
        <Card className="border-t-4 border-t-green-500">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <MessageSquare className="w-5 h-5 mr-2 text-green-500" />
              Reconocimiento Generado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <p className="text-gray-900 text-base leading-relaxed">
                    "{celebration_award.recognition.message}"
                  </p>
                </div>
                <div className="ml-4">
                  <div className="bg-white rounded-lg px-4 py-2 border border-green-300 shadow-sm">
                    <div className="text-xs text-gray-600">Puntos</div>
                    <div className="text-2xl font-bold text-green-700">
                      {celebration_award.recognition.points}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Creado el {formatDateTime(celebration_award.recognition.created_at)}
                  </span>
                </div>
                <a
                  href={`/recognitions/${celebration_award.recognition.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-green-600 hover:text-green-700 font-medium"
                >
                  Ver reconocimiento completo
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-gray-600 mb-1">ID de Reconocimiento</p>
                <p className="font-semibold text-gray-900">#{celebration_award.recognition.id}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-gray-600 mb-1">Puntos Otorgados</p>
                <p className="font-semibold text-orange-600">
                  {celebration_award.recognition.points} pts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <Calendar className="w-5 h-5 mr-2 text-indigo-500" />
            Información Temporal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <p className="text-sm font-semibold text-indigo-900">Fecha de Celebración</p>
              </div>
              <p className="text-lg font-bold text-indigo-700">
                {formatDate(celebration_award.celebrated_date)}
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <p className="text-sm font-semibold text-purple-900">Registro Creado</p>
              </div>
              <p className="text-lg font-bold text-purple-700">
                {formatDateTime(celebration_award.created_at)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Botón de regreso inferior */}
      <div className="flex justify-center">
        <Button
          onClick={goBack}
          variant="ghost"
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Historial
        </Button>
      </div>
    </div>
  );
}

CelebrationAwardShow.layout = page => <AppLayout children={page} />;

export default CelebrationAwardShow;
